import { Context } from '../context/Context';
import { Texture } from './Texture';

/**
 * Options controlling texture-array upload format + filtering.
 */
export type ArrayTextureOptions = {
  /** If true, upload as SRGB8_ALPHA8 (gamma-decoded on sample). Use for albedo maps. */
  srgb?: boolean;
  /** Generate mipmaps after upload. Defaults to true. */
  mipmaps?: boolean;
};

/**
 * Wraps a WebGL2 TEXTURE_2D_ARRAY with repeat-wrap + linear filtering. All layers must
 * share the same width/height; the caller is responsible for pre-normalizing inputs.
 */
export class ArrayTexture extends Texture {
  public readonly width: number;
  public readonly height: number;
  public readonly layerCount: number;

  public override get is2d(): boolean {
    return false;
  }

  public override get is2dArray(): boolean {
    return true;
  }

  constructor(width: number, height: number, layerCount: number) {
    super();
    this.width = width;
    this.height = height;
    this.layerCount = layerCount;
  }

  /**
   * Build an ArrayTexture from per-layer RGBA byte images. Every layer must have the same
   * dimensions as (width, height). Alpha is ignored for opaque channels but still stored.
   */
  static fromLayers(
    ctx: Context,
    layers: Uint8Array[],
    width: number,
    height: number,
    options: ArrayTextureOptions = {},
  ): ArrayTexture {
    const tex = ArrayTexture.allocate(ctx, width, height, layers.length, options);
    const gl = ctx.gl;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, tex.glTexture);
    for (let i = 0; i < layers.length; i++) {
      gl.texSubImage3D(
        gl.TEXTURE_2D_ARRAY, 0,
        0, 0, i,
        width, height, 1,
        gl.RGBA, gl.UNSIGNED_BYTE,
        layers[i],
      );
    }
    if (options.mipmaps ?? true) gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    return tex;
  }

  /**
   * Allocate storage + filter/wrap state for a TEXTURE_2D_ARRAY. Used by every
   * static factory. Returned texture is bound to TEXTURE_2D_ARRAY; caller is
   * responsible for uploading layer data and unbinding.
   *
   * Sets:
   *  - texStorage3D at (width, height, layerCount), srgb-or-not per options
   *  - REPEAT wrap on S/T
   *  - LINEAR_MIPMAP_LINEAR min + LINEAR mag when mipmaps, plain LINEAR otherwise
   *  - 16x anisotropy when the extension is available (terrain triplanar moiré
   *    cure on near-vertical surfaces)
   */
  private static allocate(
    ctx: Context,
    width: number,
    height: number,
    layerCount: number,
    options: ArrayTextureOptions,
  ): ArrayTexture {
    const { srgb = false, mipmaps = true } = options;
    const gl = ctx.gl;
    const tex = new ArrayTexture(width, height, layerCount);
    tex.glTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, tex.glTexture);
    const internalFormat = srgb ? gl.SRGB8_ALPHA8 : gl.RGBA8;
    gl.texStorage3D(
      gl.TEXTURE_2D_ARRAY,
      mipmaps ? Math.floor(Math.log2(Math.max(width, height))) + 1 : 1,
      internalFormat,
      width,
      height,
      layerCount,
    );
    if (mipmaps) {
      gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      const aniso =
        gl.getExtension('EXT_texture_filter_anisotropic') ??
        gl.getExtension('MOZ_EXT_texture_filter_anisotropic') ??
        gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
      if (aniso) {
        const maxAniso = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
        gl.texParameterf(gl.TEXTURE_2D_ARRAY, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(16, maxAniso));
      }
    } else {
      gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.REPEAT);
    return tex;
  }

  /**
   * Build an ArrayTexture from an array of HTMLImageElement / ImageBitmap. All must share
   * the same size as the first. Callers are expected to ensure images are fully loaded.
   */
  static fromImages(
    ctx: Context,
    images: (HTMLImageElement | ImageBitmap)[],
    options: ArrayTextureOptions = {},
  ): ArrayTexture {
    if (images.length === 0) {
      throw new Error('ArrayTexture.fromImages: layers must be non-empty');
    }
    const first = images[0];
    const width = first.width;
    const height = first.height;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context2d = canvas.getContext('2d', { willReadFrequently: true })!;

    const layers = images.map((img) => {
      if (img.width !== width || img.height !== height) {
        throw new Error(
          `ArrayTexture.fromImages: all layers must share dimensions; ` +
            `got ${img.width}x${img.height}, expected ${width}x${height}`,
        );
      }
      context2d.clearRect(0, 0, width, height);
      context2d.drawImage(img as CanvasImageSource, 0, 0);
      const data = context2d.getImageData(0, 0, width, height).data;
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    });

    return ArrayTexture.fromLayers(ctx, layers, width, height, options);
  }

  /**
   * Pre-allocate an ArrayTexture with `layerCount` slots and fill each layer with a
   * solid placeholder color provided by [colorAt]. Same filtering / wrap / mipmap
   * setup as {@link fromLayers}, but no caller-supplied pixel data is required —
   * layers can be replaced later via {@link updateLayer} as PNGs stream in.
   *
   * Memory: re-uses a single `width*height*4` scratch buffer across all layers, so
   * peak transient allocation is one full-size frame regardless of `layerCount`.
   */
  static withCapacity(
    ctx: Context,
    layerCount: number,
    width: number,
    height: number,
    options: ArrayTextureOptions,
    colorAt: (layerIndex: number) => readonly [number, number, number],
  ): ArrayTexture {
    if (layerCount <= 0) {
      throw new Error('ArrayTexture.withCapacity: layerCount must be positive');
    }
    const gl = ctx.gl;
    const tex = ArrayTexture.allocate(ctx, width, height, layerCount, options);

    const scratch = new Uint8Array(width * height * 4);
    for (let i = 0; i < layerCount; i++) {
      const [r, g, b] = colorAt(i);
      const r8 = Math.max(0, Math.min(255, Math.round(r * 255)));
      const g8 = Math.max(0, Math.min(255, Math.round(g * 255)));
      const b8 = Math.max(0, Math.min(255, Math.round(b * 255)));
      for (let p = 0; p < scratch.length; p += 4) {
        scratch[p] = r8;
        scratch[p + 1] = g8;
        scratch[p + 2] = b8;
        scratch[p + 3] = 255;
      }
      gl.texSubImage3D(
        gl.TEXTURE_2D_ARRAY, 0,
        0, 0, i,
        width, height, 1,
        gl.RGBA, gl.UNSIGNED_BYTE,
        scratch,
      );
    }

    if (options.mipmaps ?? true) gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    return tex;
  }

  /**
   * Replace one layer's pixel data in place. The {@link ArrayTexture} instance identity
   * is preserved so `TextureState`'s binding cache continues to hit. Does NOT regenerate
   * mipmaps — call {@link regenerateMipmaps} after a batch of updates so the cost is
   * paid once per frame, not once per layer.
   *
   * Caller is responsible for ensuring `pixelData` is in RGBA byte order and matches
   * the texture's allocated `(width, height)`. PNGs decoded through
   * `ctx.resourceLoader.loadImageData` already satisfy this when the source PNG is the
   * expected size (the backend's `SurfaceService` normalizes all uploads to 512²).
   */
  updateLayer(
    gl: WebGL2RenderingContext,
    layerIdx: number,
    pixelData: Uint8Array,
    width: number,
    height: number,
  ): void {
    if (width !== this.width || height !== this.height) {
      throw new Error(
        `ArrayTexture.updateLayer: source dimensions ${width}x${height} ` +
          `do not match texture ${this.width}x${this.height}`,
      );
    }
    if (layerIdx < 0 || layerIdx >= this.layerCount) {
      throw new Error(
        `ArrayTexture.updateLayer: layerIdx ${layerIdx} out of range [0, ${this.layerCount})`,
      );
    }
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.glTexture);
    gl.texSubImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      0, 0, layerIdx,
      width, height, 1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixelData,
    );
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
  }

  /**
   * Regenerate the mip chain for every layer. `generateMipmap` for a TEXTURE_2D_ARRAY
   * regenerates all layers atomically — there is no per-layer API, so batching
   * {@link updateLayer} calls between flushes is the only way to amortise this cost.
   */
  regenerateMipmaps(gl: WebGL2RenderingContext): void {
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.glTexture);
    gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
  }
}
